import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, FormControl } from "react-bootstrap";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { SectionHeading } from "../../components/section-heading";

import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";

import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";
import styled from "styled-components";
import { useKeycloak } from "../../components/keycloak-provider";
import SweetAlert from "sweetalert2";
import { customerMessages } from "../loyaltyCustomer/messages";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    search: {
      id: "channelManagerReservationLog.button.search",
      defaultMessage: "Search",
    },
    title: {
      id: "internalmenu.integrations.userAccessRightsFinder",
      defaultMessage: "User Access Rights Manager",
    },
    userID: {
      id: "loyaltyCustomer.id",
      defaultMessage: "User ID",
    },
    error: {
      id: "loyaltyCustomerTransactionReport.error.title",
      defaultMessage: "Error",
    },
  }),
};

const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px !important;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  > * {
    margin: 15px 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  > * {
    margin-left: 15px;
  }
`;

function UserAccessRightsFinder() {
  const config = useConfig();
  const keycloak = useKeycloak();
  const httpClient = useHttpClient(config.userManagerUrl);
  const intl = useIntl();

  const [emailAddress, setEmailAddress] = useState<string>("");
  const [shouldRequest, setShouldRequest] = useState(false);

  useEffect(() => {
    async function requestUserAccessRights() {
      try {
        const data: Blob = await httpClient.post(
          "/find-user-properties",
          {
            emailAddress,
          },
          {
            headers: {
              Authorization: keycloak.token,
            },
            responseType: "blob",
          }
        );
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${emailAddress}_access_rights.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        await SweetAlert.fire({
          icon: "error",
          text: intl.formatMessage(messages.error),
          cancelButtonText: `${intl.formatMessage(customerMessages.ok)}`,
        });
      }
      setShouldRequest(false);
    }
    if (shouldRequest) {
      requestUserAccessRights();
    }
  }, [keycloak, httpClient, emailAddress, shouldRequest, intl]);

  const handleSetEmailAddress = useCallback(e => {
    setEmailAddress(e.target.value);
  }, []);
  const handleSetShouldRequest = useCallback(() => {
    setShouldRequest(true);
  }, []);

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <FormContainer>
          <Label>
            <FormattedMessage {...messages.userID} />
          </Label>
          <FormControl
            type="email"
            placeholder="example@email.com"
            onChange={handleSetEmailAddress}
          />
          <ButtonContainer>
            <Button
              variant="primary"
              type="button"
              onClick={handleSetShouldRequest}
              disabled={shouldRequest}>
              <FormattedMessage {...messages.search} />
            </Button>
          </ButtonContainer>
        </FormContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(UserAccessRightsFinder);
